import EllipsisOutlined from "@ant-design/icons/lib/icons/EllipsisOutlined";
import { Button, Card, Dropdown, message, Modal, Table } from "antd";
import axios from "axios";
import MobileTable from "components/MobileTable/MobileTable";
import { useViewport } from "@bindeleddet/core/hooks";
import { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { RegisteredStudentData } from "../AdminSingleEventPage";

interface Props {
  registeredStudentsData: RegisteredStudentData[];
  loading: boolean;
  onStudentRegistered: (studentId: number) => void;
  eventId: number;
}

const Waitlist: React.FC<Props> = ({ registeredStudentsData, loading, eventId, onStudentRegistered }) => {
  const { width } = useViewport();
  const history = useHistory();
  const [loadingModal, setLoadingModal] = useState(false);

  const columns = [
    {
      title: "#",
      dataIndex: "number",
      key: "number",
      sorter: false,
    },
    {
      title: "Navn",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: RegisteredStudentData) => {
        return <Link to={`/admin/students/${record.id}`}>{name}</Link>;
      },
    },
    {
      title: "Brukernavn",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Linje",
      dataIndex: "study_programme_short_name",
      key: "study_programme",
    },
    {
      title: "Årstrinn",
      dataIndex: "year_level",
      key: "year_level",
    },
    {
      title: "Telefon",
      dataIndex: "mobile_number",
      key: "mobile_number",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Allergier",
      dataIndex: "allergies",
      key: "allergies",
    },
    {
      title: "Påmeldt",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a: RegisteredStudentData, b: RegisteredStudentData) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (date: string) => {
        return new Date(date).toLocaleString("nb-NO", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      },
    },
    {
      key: "action",
      name: "action",
      dataIndex: "id",
      render: (studentId: number, record: RegisteredStudentData) => {
        const handleDeregister = () => {
          Modal.confirm({
            title: `Meld på arrangementet`,
            content: `Er du sikker på at du vil overstyre venteliste-logikken og manuelt melde ${record.name} på arrangementet?`,
            onOk: () => {
              setLoadingModal(true);
              return axios
                .post(`/events/${eventId}/admin_register/`, {
                  student: studentId,
                })
                .then(() => {
                  message.success(`Studenten er meldt på arrangementet`);
                  onStudentRegistered(studentId);
                })
                .catch(() => {
                  message.error("Studenten ble ikke meldt på arrangementet");
                })
                .finally(() => {
                  setLoadingModal(false);
                });
            },
            okButtonProps: {
              danger: true,
              loading: loadingModal,
            },
            okText: "Meld på",
            cancelText: "Avbryt",
          });
        };

        const menuItems = [{ key: "2", label: "Meld på arrangement", onClick: handleDeregister }];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <Button icon={<EllipsisOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <Card title="Venteliste" style={{ width: "100%" }}>
      {width > 768 ? (
        <Table
          style={{ width: "100%" }}
          dataSource={registeredStudentsData}
          columns={columns}
          pagination={false}
          loading={loading}
          locale={{ emptyText: loading ? "Laster inn venteliste..." : "Ingen på venteliste" }}
        />
      ) : (
        <MobileTable
          data={registeredStudentsData}
          columns={[
            {
              title: "#",
              dataIndex: "number",
              key: "number",
            },
            {
              title: "Navn",
              dataIndex: "name",
              key: "name",
            },
            {
              title: "Linje",
              dataIndex: "study_programme_name",
              key: "study_programme",
            },
            {
              title: "Årstrinn",
              dataIndex: "year_level",
              key: "year_level",
            },
            {
              title: "Telefon",
              dataIndex: "mobile_number",
              key: "mobile_number",
            },
            {
              title: "Email",
              dataIndex: "email",
              key: "email",
            },
            {
              title: "Allergier",
              dataIndex: "allergies",
              key: "allergies",
            },
            {
              title: "Påmeldt",
              dataIndex: "created_at",
              key: "created_at",
              render: (date: unknown) => {
                if (!date || typeof date !== "string") return "";
                return new Date(date).toLocaleString("nb-NO", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                });
              },
            },
          ]}
          loading={loading}
          onClick={id => history.push(`/admin/students/${id}/`)}
          emptyText="Ingen på venteliste"
          actions={record => {
            const handleRegister = () => {
              Modal.confirm({
                title: `Meld på arrangementet`,
                content: `Er du sikker på at du vil overstyre venteliste-logikken og manuelt melde ${record.name} på arrangementet?`,
                onOk: () => {
                  setLoadingModal(true);
                  return axios
                    .post(`/events/${eventId}/admin_register/`, {
                      student: record.id,
                    })
                    .then(() => {
                      message.success(`Studenten er meldt på arrangementet`);
                      onStudentRegistered(record.id);
                    })
                    .catch(() => {
                      message.error("Studenten ble ikke meldt på arrangementet");
                    })
                    .finally(() => {
                      setLoadingModal(false);
                    });
                },
                okButtonProps: { danger: true, loading: loadingModal },
                okText: "Meld på",
                cancelText: "Avbryt",
              });
            };

            return (
              <Dropdown
                menu={{ items: [{ key: "1", label: "Meld på arrangement", onClick: handleRegister }] }}
                trigger={["click"]}
              >
                <Button icon={<EllipsisOutlined />} />
              </Dropdown>
            );
          }}
        />
      )}
    </Card>
  );
};

export default Waitlist;
